import type {
  AncestryFact,
  AncestryReportData,
  AncestryReportLifeEvent,
  AncestryReportSectionCounts,
  PersonRelative,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toString(value: unknown): string {
  return value == null ? '' : String(value);
}

function toNumberOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeLifeEvent(raw: unknown): AncestryReportLifeEvent {
  const r = asRecord(raw);
  if (!r) {
    return {};
  }
  return {
    year: r.year ? toString(r.year) : undefined,
    date: r.date ? toString(r.date) : undefined,
    place: r.place ? toString(r.place) : undefined,
  };
}

// Cleans up "data:,Some+Encoded+Label" type strings from FamilySearch facts.
function cleanFactType(raw: string): string {
  if (!raw.startsWith('data:,')) {
    return raw;
  }
  try {
    return decodeURIComponent(raw.slice(6).replace(/\+/g, ' '));
  } catch {
    return raw.slice(6).replace(/\+/g, ' ');
  }
}

function mapFacts(raw: unknown): AncestryFact[] {
  const r = asRecord(raw);
  return asArray(r?.facts)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      type: cleanFactType(toString(item.type)),
      date: toString(item.date),
      place: toString(item.place),
      value: toString(item.value),
    }))
    .filter((f) => f.type.length > 0);
}

function mapRelatives(raw: unknown): PersonRelative[] {
  const r = asRecord(raw);
  return asArray(r?.relatives)
    .map(asRecord)
    .filter((item): item is Record<string, unknown> => item !== null)
    .map((item) => ({
      id: toString(item.id),
      name: toString(item.name),
      address: toString(item.address),
      relationship: toString(item.relationship),
      aliases: asArray(item.aliases).map(toString).filter(Boolean),
      age: toNumberOrNull(item.age),
    }))
    .filter((item) => item.name.length > 0);
}

export function mapAncestryReportPayload(
  value: Record<string, unknown>
): AncestryReportData {
  // New shape nests overview fields; fall back to top-level for old payloads.
  const overview = asRecord(value.overview) ?? value;

  // New shape uses snake_case; fall back to camelCase for old payloads.
  const relativesSource = value.family_and_ancestry ?? value.familyAndAncestry;

  const facts = mapFacts(value.facts);
  const relatives = mapRelatives(relativesSource);

  const counts: AncestryReportSectionCounts = {
    relatives: relatives.length,
    additionalFacts: facts.length,
    memories: 0,
  };

  const aliases = asArray(overview.aliases).map(toString).filter(Boolean);

  const rawSections = Array.isArray(value.availableSections)
    ? value.availableSections
    : [];
  const availableSections = rawSections.filter(
    (s): s is string => typeof s === 'string'
  );

  return {
    name: toString(overview.name),
    aliases,
    birth: normalizeLifeEvent(overview.birth),
    death: normalizeLifeEvent(overview.death),
    burial: normalizeLifeEvent(overview.burial),
    sectionCounts: counts,
    availableSections,
    permalink: value.permalink ? toString(value.permalink) : undefined,
    facts: facts.length > 0 ? facts : undefined,
    relatives: relatives.length > 0 ? relatives : undefined,
  };
}
